import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  CheckCircle2,
  XCircle,
  Zap,
  ArrowRight,
  QrCode,
  BookOpen,
  GraduationCap,
  Briefcase,
  Mail,
  MessageSquare,
  Globe,
  Clock,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

export const NixlevMaster60s: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Color Palette
  const bgCream = "#F7F5F0";
  const greenForest = "#1B3B2B";
  const greenEmerald = "#10B981";
  const goldAccent = "#D4A346";
  const textDark = "#1A1A1A";
  const textMuted = "#6B7280";

  // Micro floating motion
  const subtleFloat = Math.sin(frame / 20) * 5;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCream,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* 1. Full 1-Minute Gemini TTS Voiceover (Orus) */}
      <Audio src={staticFile("nixlev_orus_1min.mp3")} />

      {/* 2. Micro SFX Timing */}
      {frame === 0 && <Audio src={staticFile("whoosh.mp3")} volume={0.4} />}
      {frame === 80 && <Audio src={staticFile("click.mp3")} volume={0.7} />}
      {frame === 140 && <Audio src={staticFile("click.mp3")} volume={0.9} />}

      {frame === 240 && <Audio src={staticFile("whoosh.mp3")} volume={0.4} />}
      {frame === 360 && <Audio src={staticFile("click.mp3")} volume={0.6} />}

      {frame === 540 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 600 && <Audio src={staticFile("click.mp3")} volume={0.6} />}

      {frame === 780 && <Audio src={staticFile("whoosh.mp3")} volume={0.4} />}
      {frame === 900 && <Audio src={staticFile("click.mp3")} volume={0.5} />}

      {frame === 1020 && <Audio src={staticFile("whoosh.mp3")} volume={0.4} />}
      {frame === 1140 && <Audio src={staticFile("click.mp3")} volume={0.7} />}
      {frame === 1240 && <Audio src={staticFile("click.mp3")} volume={0.7} />}
      {frame === 1320 && <Audio src={staticFile("click.mp3")} volume={0.8} />}

      {frame === 1440 && <Audio src={staticFile("whoosh.mp3")} volume={0.4} />}
      {frame === 1560 && <Audio src={staticFile("click.mp3")} volume={0.6} />}

      {/* =========================================================================
          BEAT 1: THE FRICTION CALLOUT (Frames 0 - 240 / 0.0s - 8.0s)
          "Berapa banyak calon pembeli yang batal bayar cuma gara-gara bio link kamu ribet dan harus chat manual buat minta nomor rekening?"
          ========================================================================= */}
      {frame < 240 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 50px",
          }}
        >
          {/* Eyebrow badge */}
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#FEE2E2",
              color: "#DC2626",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: "0.05em",
              marginBottom: 35,
              textTransform: "uppercase",
            }}
          >
            Masalah Nyata Kreator
          </div>

          <h1
            style={{
              fontSize: 54,
              fontWeight: 800,
              textAlign: "center",
              lineHeight: 1.25,
              marginBottom: 50,
              letterSpacing: "-0.02em",
            }}
          >
            Berapa banyak pembeli kabur karena <span style={{ color: "#DC2626" }}>bio link ribet?</span>
          </h1>

          {/* Simulated Mobile Phone */}
          <div
            style={{
              width: 520,
              backgroundColor: "#FFFFFF",
              borderRadius: 44,
              border: "12px solid #E5E7EB",
              boxShadow: "0 30px 60px rgba(0,0,0,0.12)",
              padding: 30,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${subtleFloat}px)`,
            }}
          >
            {/* Phone avatar */}
            <div
              style={{
                width: 90,
                height: 90,
                borderRadius: "50%",
                backgroundColor: "#E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 36,
                fontWeight: 700,
                color: "#6B7280",
                marginBottom: 16,
              }}
            >
              @kreator
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 25 }}>
              Linktree / Bio Link Biasa
            </div>

            {/* Ineffective Button Stack */}
            {["Download E-Book (Chat WA)", "Beli Template (Transfer BCA)", "Jadwal Konsultasi (DM IG)"].map(
              (btn, i) => (
                <div
                  key={i}
                  style={{
                    width: "100%",
                    padding: "18px 20px",
                    backgroundColor: "#F3F4F6",
                    borderRadius: 16,
                    fontSize: 20,
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: 12,
                    textAlign: "center",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  {btn}
                </div>
              )
            )}

            {/* WA Waiting Bubble */}
            {frame >= 70 && (
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#DCF8C6",
                  padding: "16px 20px",
                  borderRadius: 18,
                  marginTop: 15,
                  fontSize: 19,
                  color: "#1F2937",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  animation: "fadeIn 0.3s ease",
                }}
              >
                <Clock size={24} color="#059669" />
                <div>
                  <div style={{ fontWeight: 700 }}>"Kak mau order, minta no rek ya..."</div>
                  <div style={{ fontSize: 16, color: "#6B7280" }}>Belum dibalas admin (3 jam) ⏳</div>
                </div>
              </div>
            )}
          </div>

          {/* Red Stamp Silang */}
          {frame >= 130 && (
            <div
              style={{
                position: "absolute",
                top: "48%",
                transform: `rotate(-14deg) scale(${spring({
                  frame: frame - 130,
                  fps,
                  config: { damping: 12, stiffness: 200 },
                })})`,
                backgroundColor: "rgba(220, 38, 38, 0.95)",
                color: "#FFFFFF",
                padding: "20px 45px",
                borderRadius: 24,
                fontSize: 42,
                fontWeight: 900,
                letterSpacing: "0.08em",
                boxShadow: "0 20px 50px rgba(220, 38, 38, 0.4)",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <XCircle size={46} color="#FFFFFF" />
              KONVERSI HANCUR!
            </div>
          )}
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 2: THE DILEMMA (Frames 240 - 540 / 8.0s - 18.0s)
          "Bio link biasa gak didesain untuk jualan. Tapi mau bikin website sendiri, kamu harus bayar hosting mahal, setup payment gateway, dan pusing mikirin koding."
          ========================================================================= */}
      {frame >= 240 && frame < 540 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 50px",
          }}
        >
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#FEF3C7",
              color: "#D97706",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: "0.05em",
              marginBottom: 35,
              textTransform: "uppercase",
            }}
          >
            Dilema Bikin Website Sendiri
          </div>

          <h2
            style={{
              fontSize: 52,
              fontWeight: 800,
              textAlign: "center",
              lineHeight: 1.25,
              marginBottom: 50,
            }}
          >
            Mau Bikin Website Sendiri? <br />
            <span style={{ color: "#DC2626" }}>Ribet & Mahal Banget.</span>
          </h2>

          <div style={{ width: "100%", maxWidth: 840, display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Card 1: Hosting */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 28,
                padding: 35,
                border: "2px solid #F3F4F6",
                boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 25,
              }}
            >
              <div
                style={{
                  width: 75,
                  height: 75,
                  borderRadius: 22,
                  backgroundColor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <XCircle size={42} color="#DC2626" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 30, fontWeight: 800, color: textDark }}>
                  Sewa Hosting & Domain Mahal
                </div>
                <div style={{ fontSize: 22, color: textMuted, marginTop: 6 }}>
                  Rp 450.000 – Rp 1.200.000 / tahun, belum tentu ada penjualan.
                </div>
              </div>
            </div>

            {/* Card 2: Coding */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 28,
                padding: 35,
                border: "2px solid #F3F4F6",
                boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 25,
              }}
            >
              <div
                style={{
                  width: 75,
                  height: 75,
                  borderRadius: 22,
                  backgroundColor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <XCircle size={42} color="#DC2626" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 30, fontWeight: 800, color: textDark }}>
                  Pusing Koding & Desain Rumit
                </div>
                <div style={{ fontSize: 22, color: textMuted, marginTop: 6 }}>
                  WordPress plugin sering crash, setting database bikin pusing.
                </div>
              </div>
            </div>

            {/* Card 3: Payment Gateway */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 28,
                padding: 35,
                border: "2px solid #F3F4F6",
                boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 25,
              }}
            >
              <div
                style={{
                  width: 75,
                  height: 75,
                  borderRadius: 22,
                  backgroundColor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <XCircle size={42} color="#DC2626" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 30, fontWeight: 800, color: textDark }}>
                  Setup Payment Gateway Ribet
                </div>
                <div style={{ fontSize: 22, color: textMuted, marginTop: 6 }}>
                  Butuh verifikasi berbelit, biaya integrasi, dan setup manual.
                </div>
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 3: THE NIXLEV REVEAL (Frames 540 - 780 / 18.0s - 26.0s)
          "Kenalkan: Nixlev dot com. Microsite penjualan otomatis yang dibuat khusus untuk kreator, freelancer, dan pebisnis digital."
          ========================================================================= */}
      {frame >= 540 && frame < 780 && (
        <AbsoluteFill
          style={{
            backgroundColor: greenForest,
            color: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 50px",
          }}
        >
          {/* Eyebrow */}
          <div
            style={{
              padding: "10px 28px",
              backgroundColor: "rgba(255,255,255,0.12)",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: "0.06em",
              marginBottom: 40,
              color: goldAccent,
              textTransform: "uppercase",
            }}
          >
            Solusi Semua Masalah Itu
          </div>

          {/* Official HD Logo */}
          <div
            style={{
              transform: `scale(${spring({
                frame: frame - 540,
                fps,
                config: { damping: 15.35, stiffness: 115.0 },
              })})`,
              marginBottom: 35,
            }}
          >
            <Img
              src={staticFile("nixlev_logo.png")}
              style={{
                width: 220,
                height: 220,
                objectFit: "contain",
                filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.3))",
              }}
            />
          </div>

          <h1
            style={{
              fontSize: 68,
              fontWeight: 900,
              letterSpacing: "-0.03em",
              marginBottom: 20,
              textAlign: "center",
            }}
          >
            Nixlev.com
          </h1>

          <p
            style={{
              fontSize: 34,
              fontWeight: 500,
              color: "#E2E8F0",
              textAlign: "center",
              maxWidth: 820,
              lineHeight: 1.4,
              marginBottom: 50,
            }}
          >
            Microsite penjualan otomatis untuk monetisasi karya dan jasamu.
          </p>

          {/* 3 Audience Badges */}
          <div style={{ display: "flex", gap: 18 }}>
            {[
              { label: "Kreator Konten", icon: Sparkles },
              { label: "Freelancer Jasa", icon: Briefcase },
              { label: "Pebisnis Digital", icon: Zap },
            ].map((badge, idx) => {
              const IconComp = badge.icon;
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "16px 26px",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 22,
                    fontSize: 22,
                    fontWeight: 700,
                  }}
                >
                  <IconComp size={24} color={goldAccent} />
                  {badge.label}
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 4: REAL DESKTOP SCREENSHOT TOUR (Frames 780 - 1020 / 26.0s - 34.0s)
          "Dalam 5 menit, kamu bisa bikin halaman penawaran yang elegan, cepat, dan siap terima pembayaran QRIS secara instan."
          ========================================================================= */}
      {frame >= 780 && frame < 1020 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 40px",
          }}
        >
          {/* Top Pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "12px 28px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 800,
              marginBottom: 30,
            }}
          >
            <Clock size={26} />
            Halaman Siap Dalam 5 Menit
          </div>

          <h2
            style={{
              fontSize: 50,
              fontWeight: 800,
              textAlign: "center",
              lineHeight: 1.25,
              marginBottom: 40,
            }}
          >
            Tampilan Desktop Elegan & Cepat
          </h2>

          {/* REAL DESKTOP BROWSER SCREENSHOT WINDOW */}
          <div
            style={{
              width: 960,
              height: 1200,
              backgroundColor: "#FFFFFF",
              borderRadius: 28,
              border: "1px solid #E5E7EB",
              boxShadow: "0 35px 80px rgba(0,0,0,0.16)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transform: `scale(${interpolate(frame, [780, 840], [0.92, 1.0], {
                easing: Easing.bezier(0.16, 1, 0.3, 1),
                extrapolateRight: "clamp",
              })})`,
            }}
          >
            {/* macOS Browser Chrome Bar */}
            <div
              style={{
                height: 54,
                backgroundColor: "#F9FAFB",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                padding: "0 20px",
                gap: 14,
              }}
            >
              {/* Traffic Light Dots */}
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>

              {/* URL Address Bar */}
              <div
                style={{
                  flex: 1,
                  height: 34,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                <Globe size={16} color="#10B981" />
                https://nixlev.com
              </div>
            </div>

            {/* REAL DESKTOP SCREENSHOT IMAGE WITH SMOOTH VERTICAL TOUR */}
            <div
              style={{
                flex: 1,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Img
                src={staticFile("nixlev_desktop_tall.png")}
                style={{
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "top center",
                  transform: `translateY(${interpolate(
                    frame,
                    [780, 1020],
                    [0, -420],
                    { easing: Easing.inOut(Easing.quad) }
                  )}px)`,
                }}
              />

              {/* Instant QRIS Badge Over Desktop */}
              <div
                style={{
                  position: "absolute",
                  bottom: 40,
                  right: 40,
                  backgroundColor: greenForest,
                  color: "#FFFFFF",
                  padding: "16px 26px",
                  borderRadius: 20,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  fontSize: 22,
                  fontWeight: 800,
                }}
              >
                <Zap size={26} color={goldAccent} />
                Checkout QRIS Otomatis
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 5: DUAL DELIVERY (EMAIL + WHATSAPP) (Frames 1020 - 1440 / 34.0s - 48.0s)
          "Tinggal upload e-book, template, kelas online, atau jasamu. Begitu pembeli selesai scan QRIS, produk dan aksesnya langsung dikirim otomatis ke email dan WhatsApp pembeli detik itu juga. 24 jam nonstop, tanpa kamu perlu repot cek mutasi bank atau kirim file manual."
          ========================================================================= */}
      {frame >= 1020 && frame < 1440 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 40px",
          }}
        >
          {/* Eyebrow */}
          <div
            style={{
              padding: "10px 26px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: "0.05em",
              marginBottom: 30,
              textTransform: "uppercase",
            }}
          >
            Pengiriman Otomatis 24 Jam Nonstop
          </div>

          <h2
            style={{
              fontSize: 52,
              fontWeight: 800,
              textAlign: "center",
              lineHeight: 1.25,
              marginBottom: 40,
            }}
          >
            Bayar Sekali via QRIS ➔ <br />
            <span style={{ color: greenForest }}>Kirim Otomatis ke Email & WhatsApp</span>
          </h2>

          <div style={{ width: "100%", maxWidth: 880, display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Step 1: Scan QRIS */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 26,
                padding: "26px 32px",
                border: "2px solid #E5E7EB",
                boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 24,
              }}
            >
              <div
                style={{
                  width: 70,
                  height: 70,
                  borderRadius: 20,
                  backgroundColor: "#F3F4F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <QrCode size={40} color={greenForest} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 28, fontWeight: 800 }}>Pembeli Scan QRIS Dinamis</div>
                <div style={{ fontSize: 20, color: textMuted }}>
                  Nominal pas otomatis, terverifikasi detik itu juga.
                </div>
              </div>
              <CheckCircle2 size={36} color="#10B981" />
            </div>

            {/* Step 2: Instant Email Delivery Notification */}
            {frame >= 1140 && (
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: 26,
                  padding: "26px 32px",
                  border: "2px solid #3B82F6",
                  boxShadow: "0 20px 45px rgba(59, 130, 246, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                  transform: `translateY(${interpolate(frame, [1140, 1170], [40, 0], {
                    easing: Easing.bezier(0.16, 1, 0.3, 1),
                    extrapolateRight: "clamp",
                  })}px)`,
                }}
              >
                <div
                  style={{
                    width: 70,
                    height: 70,
                    borderRadius: 20,
                    backgroundColor: "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Mail size={38} color="#2563EB" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#1E3A8A" }}>
                    Terkirim Otomatis ke Email
                  </div>
                  <div style={{ fontSize: 20, color: "#4B5563" }}>
                    Invoice resmi + link download file berlisensi instan.
                  </div>
                </div>
                <span
                  style={{
                    backgroundColor: "#DBEAFE",
                    color: "#1D4ED8",
                    padding: "8px 16px",
                    borderRadius: 14,
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  INSTANT
                </span>
              </div>
            )}

            {/* Step 3: Instant WhatsApp Bot Delivery Notification */}
            {frame >= 1240 && (
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: 26,
                  padding: "26px 32px",
                  border: "2px solid #22C55E",
                  boxShadow: "0 20px 45px rgba(34, 197, 94, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                  transform: `translateY(${interpolate(frame, [1240, 1270], [40, 0], {
                    easing: Easing.bezier(0.16, 1, 0.3, 1),
                    extrapolateRight: "clamp",
                  })}px)`,
                }}
              >
                <div
                  style={{
                    width: 70,
                    height: 70,
                    borderRadius: 20,
                    backgroundColor: "#DCFCE7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MessageSquare size={38} color="#15803D" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#14532D" }}>
                    Terkirim Otomatis ke WhatsApp
                  </div>
                  <div style={{ fontSize: 20, color: "#4B5563" }}>
                    Bot langsung kirim file & instruksi ke nomor WA pembeli.
                  </div>
                </div>
                <span
                  style={{
                    backgroundColor: "#DCFCE7",
                    color: "#15803D",
                    padding: "8px 16px",
                    borderRadius: 14,
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  24/7 BOT
                </span>
              </div>
            )}

            {/* No Manual Checking Tag */}
            {frame >= 1340 && (
              <div
                style={{
                  backgroundColor: "#F3F4F6",
                  padding: "18px 24px",
                  borderRadius: 18,
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#374151",
                  textAlign: "center",
                }}
              >
                ⚡ Kamu gak perlu begadang cek mutasi bank atau kirim file manual!
              </div>
            )}
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 6: THE NO-BRAINER CTA & CLOSING (Frames 1440 - 1800 / 48.0s - 60.0s)
          "Tanpa sewa hosting. Tanpa ribet koding. Berhenti jualan manual. Bikin halaman bisnismu sekarang di nixlev dot com."
          ========================================================================= */}
      {frame >= 1440 && (
        <AbsoluteFill
          style={{
            backgroundColor: bgCream,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 50px",
          }}
        >
          {/* Logo Brand Settle */}
          <Img
            src={staticFile("nixlev_logo.png")}
            style={{
              width: 140,
              height: 140,
              objectFit: "contain",
              marginBottom: 25,
            }}
          />

          <h1
            style={{
              fontSize: 64,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.2,
              marginBottom: 40,
              letterSpacing: "-0.02em",
            }}
          >
            Berhenti Jualan Manual. <br />
            <span style={{ color: greenForest }}>Ubah Bio Link Jadi Mesin Cuan.</span>
          </h1>

          {/* 3 Core Trust Badges */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 18,
              width: "100%",
              maxWidth: 720,
              marginBottom: 50,
            }}
          >
            {[
              "100% Tanpa Biaya Sewa Hosting",
              "Checkout QRIS Otomatis Terhubung",
              "Pengiriman File Instan via WhatsApp & Email",
            ].map((text, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  fontSize: 24,
                  fontWeight: 700,
                  color: textDark,
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    backgroundColor: "#DCFCE7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle2 size={26} color="#16A34A" />
                </div>
                {text}
              </div>
            ))}
          </div>

          {/* Forest Green CTA Button */}
          <div
            style={{
              backgroundColor: greenForest,
              color: "#FFFFFF",
              padding: "26px 54px",
              borderRadius: 30,
              fontSize: 32,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: 18,
              boxShadow: "0 25px 60px rgba(27, 59, 43, 0.35)",
              transform: `scale(${1 + Math.sin(frame / 12) * 0.025})`,
            }}
          >
            <span>Mulai Buat Gratis di nixlev.com</span>
            <ArrowRight size={36} color={goldAccent} />
          </div>

          <div
            style={{
              marginTop: 35,
              fontSize: 22,
              fontWeight: 600,
              color: textMuted,
            }}
          >
            Daftar gratis dalam 5 menit ➔ nixlev.com
          </div>
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
};
